// DEPLOY-PROBE-bump

/**
 * Login Component
 * 
 * Authentication component for the payslip system.
 * Handles login for both office employees and construction workers.
 * 
 * Features:
 * - Dual authentication system (tries office employee first, then construction worker)
 * - Form validation
 * - Error handling and display
 * - Session management via sessionStorage (cleared when tab closes)
 * - Navigation based on user type (Admin, Employee, ConstructionWorker)
 * 
 * @component
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import EmployeeService, { LoginResponse } from '../../services/EmployeeService';
import ConstructionWorkerService, { LoginResponse as ConstructionLoginResponse } from '../../services/ConstructionWorkerService';
import * as RecoveryAuthService from '../../services/RecoveryAuthService';
import logo from '../../resources/logo.png';

/**
 * Props interface for Login component
 */
interface LoginProps {
  onLoginSuccess: (employee: any) => void; // Callback function called when login is successful
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  // React Router navigation hook for programmatic navigation
  const navigate = useNavigate();
  
  // State for employee code input field
  const [empCode, setEmpCode] = useState('');
  
  // State for password input field
  const [password, setPassword] = useState('');
  
  // Loading state to disable form and show loading indicator during authentication
  const [isLoading, setIsLoading] = useState(false);
  
  // Error message state to display authentication errors to user
  const [error, setError] = useState('');

  // Admin account recovery flow (recovery ID + PIN on login -> security questions -> new password)
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryId, setRecoveryId] = useState('');
  const [recoveryPin, setRecoveryPin] = useState('');
  const [recoveryQuestions, setRecoveryQuestions] = useState<{ q1: string; q2: string; q3: string } | null>(null);
  const [answer1, setAnswer1] = useState('');
  const [answer2, setAnswer2] = useState('');
  const [answer3, setAnswer3] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  /**
   * Handles login form submission.
   * Implements a dual authentication system:
   * 1. First attempts to authenticate as an office employee
   * 2. If that fails, attempts to authenticate as a construction worker
   * 
   * On successful authentication:
   * - Stores employee data in sessionStorage for session persistence
   * - Calls the onLoginSuccess callback
   * - Navigates to the appropriate page based on user type
   * 
   * @param e - Form submission event
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empCode.trim() || !password.trim()) {
      setError('従業員コードとパスワードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Try admin account recovery first (recovery ID + PIN in emp code / password fields)
      const recoveryRes = await RecoveryAuthService.getRecoveryQuestions(empCode.trim(), password);
      if (recoveryRes.success && recoveryRes.question1) {
        setRecoveryId(empCode.trim());
        setRecoveryPin(password);
        setRecoveryQuestions({
          q1: recoveryRes.question1,
          q2: recoveryRes.question2 ?? '',
          q3: recoveryRes.question3 ?? '',
        });
        setRecoveryMode(true);
        setRecoveryError('');
        setRecoverySuccess(false);
        setPassword('');
        setIsLoading(false);
        return;
      }

      let response: any = null;
      let employee: any = null;

      try {
        const officeResponse: LoginResponse = await EmployeeService.login(empCode.trim(), password);
        if (officeResponse.success && officeResponse.employee) {
          response = officeResponse;
          employee = officeResponse.employee;
        }
      } catch {
        // try construction worker next
      }

      if (!employee) {
        try {
          const constructionResponse: ConstructionLoginResponse = await ConstructionWorkerService.login(empCode.trim(), password);
          if (constructionResponse.success && constructionResponse.employee) {
            employee = constructionResponse.employee;
            response = constructionResponse;
          }
        } catch {
          // show generic error below
        }
      }

      if (response && response.success && employee) {
        sessionStorage.setItem('currentEmployee', JSON.stringify(employee));
        onLoginSuccess(employee);
        if (employee.userType === '管理者' || employee.userType === '現場管理者' || employee.userType === '担当現場管理者') {
          navigate('/Admin');
        } else if (employee.userType === '従業員') {
          navigate('/Employee');
        } else if (employee.userType === 'construction worker') {
          navigate('/ConstructionWorker');
        } else {
          navigate('/');
        }
      } else {
        setError('従業員コードまたはパスワードが正しくありません');
      }
    } catch {
      setError('ログイン中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryBack = () => {
    setRecoveryMode(false);
    setRecoveryQuestions(null);
    setRecoveryId('');
    setRecoveryPin('');
    setAnswer1('');
    setAnswer2('');
    setAnswer3('');
    setNewPassword('');
    setConfirmPassword('');
    setRecoveryError('');
    setRecoverySuccess(false);
  };

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryError('');
    if (!answer1.trim() || !answer2.trim() || !answer3.trim()) {
      setRecoveryError('3つの質問への回答をすべて入力してください。');
      return;
    }
    if (newPassword.length < 6) {
      setRecoveryError('新しいパスワードは6文字以上で入力してください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setRecoveryError('新しいパスワードと確認が一致しません。');
      return;
    }
    setRecoverySubmitting(true);
    try {
      const res = await RecoveryAuthService.resetPasswordWithRecovery(
        recoveryId,
        recoveryPin,
        answer1,
        answer2,
        answer3,
        newPassword
      );
      if (res.success) {
        setRecoverySuccess(true);
      } else {
        setRecoveryError(res.message || 'パスワードの変更に失敗しました。');
      }
    } catch {
      setRecoveryError('パスワードの変更に失敗しました。');
    } finally {
      setRecoverySubmitting(false);
    }
  };

  // Recovery success: show message and button to return to login
  if (recoveryMode && recoverySuccess) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} className="login-logo" alt="会社ロゴ" />
            <h1 className="login-title">パスワードを変更しました</h1>
            <p className="recovery-success-text">新しいパスワードでログインしてください。</p>
          </div>
          <div className="login-form">
            <button type="button" className="login-button" onClick={handleRecoveryBack}>
              ログイン画面に戻る
            </button>
          </div>
          <div className="login-footer">
            <p className="copyright">© 2026 株式会社西行土木. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  // Recovery form: security questions + new password
  if (recoveryMode && recoveryQuestions) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} className="login-logo" alt="会社ロゴ" />
            <h1 className="login-title">管理者アカウントの回復</h1>
            <p>秘密の質問に答えて、新しいパスワードを設定してください</p>
          </div>
          <form onSubmit={handleRecoverySubmit} className="login-form">
            <div className="form-group">
              <label className="recovery-question-label">{recoveryQuestions.q1}</label>
              <input
                type="text"
                value={answer1}
                onChange={(e) => setAnswer1(e.target.value)}
                placeholder="回答1"
                disabled={recoverySubmitting}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="recovery-question-label">{recoveryQuestions.q2}</label>
              <input
                type="text"
                value={answer2}
                onChange={(e) => setAnswer2(e.target.value)}
                placeholder="回答2"
                disabled={recoverySubmitting}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="recovery-question-label">{recoveryQuestions.q3}</label>
              <input
                type="text"
                value={answer3}
                onChange={(e) => setAnswer3(e.target.value)}
                placeholder="回答3"
                disabled={recoverySubmitting}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">新しいパスワード（6文字以上）</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="新しいパスワード"
                disabled={recoverySubmitting}
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">新しいパスワード（確認）</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="確認"
                disabled={recoverySubmitting}
                autoComplete="new-password"
              />
            </div>
            {recoveryError && <div className="error-message">{recoveryError}</div>}
            <div className="recovery-actions">
              <button type="button" className="login-button login-button-secondary" onClick={handleRecoveryBack} disabled={recoverySubmitting}>
                戻る
              </button>
              <button type="submit" className="login-button" disabled={recoverySubmitting}>
                {recoverySubmitting ? '変更中...' : 'パスワードを変更する'}
              </button>
            </div>
          </form>
          <div className="login-footer">
            <p className="copyright">© 2026 株式会社西行土木. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} className="login-logo" alt="会社ロゴ" />
          <h1 className="login-title">株式会社西行土木</h1>
          <h2 className="login-subtitle">給与明細システム</h2>
          <p>従業員コードとパスワードを入力してください</p>
        </div>
        
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="empCode">従業員コード</label>
            <input
              type="text"
              id="empCode"
              value={empCode}
              onChange={(e) => setEmpCode(e.target.value)}
              placeholder="例: 0001"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">パスワード</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワードを入力"
              disabled={isLoading}
              required
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>
        
        <div className="login-footer">
          <p className="copyright">© 2026 株式会社西行土木. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
